import { AiCountMarks } from "./ai-count-marks";
import { AiCountStartForm } from "./ai-count-start-form";
import { AiCountReviewEditor } from "./ai-count-review-editor";
import { AiCountAudit } from "./ai-count-audit";
import { AiCountIssues } from "./ai-count-issues";
import type { AiBatchReview, AiCountPage, AiCountTag, AiLatestReview, AiMeasurementBasis } from "./ai-count-types";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { AiReviewGroups, type AiReviewLocation } from "./ai-review-groups";
import { createPortal } from "react-dom";
import { AiPageScaleStatus } from "./ai-page-scale-status";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, type StatusKey } from "@/components/status-badge";
import { useAiDebugMode } from "./use-ai-debug-mode";
import { AiDebugPanel } from "./ai-debug-panel";
import { focusViewportOnPageBounds } from "./use-takeoff-viewer-focus";

export const AI_COUNTS_ENABLED =
  import.meta.env.VITE_TAKEOFF_AI_ENABLED === "true";

function continueLabel(totalTasks: number) {
  return totalTasks === 0 ? "Retry page preparation" : "Continue unfinished regions";
}

/** The persisted normalized point is the sole source for the displayed fixture center. */
export function AiCountOverlay({
  jobId,
  planDocumentId,
  pageNumber,
  tags,
  data,
  showMarks,
  interactiveMarks,
  onPlacementChange,
  focusRequest,
  onFocusMeasurement,
  onTraceMeasurement,
  onAddMeasurement,
  onDrawCutout,
  onRemoveCutout,
  controlsContainer,
  nativeSelection = [],
  preferredReviewTagId,
  onNavigateReview,
  onSelectNative,
  onReviewBatch,
  onUndoLatestReview,
  lineageSessionId,
}: {
  controlsContainer: HTMLElement | null;
  nativeSelection?: Id<"takeoffPilotMeasurements">[];
  preferredReviewTagId?: Id<"takeoffPilotExtractedTags">;
  onNavigateReview?: (location: AiReviewLocation) => void;
  onSelectNative?: (ids: Id<"takeoffPilotMeasurements">[]) => void;
  onReviewBatch: (args: AiBatchReview) => Promise<void>;
  onUndoLatestReview: (action: AiLatestReview) => Promise<void>;
  lineageSessionId?: string;
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId: Id<"takeoffPilotPlanDocuments">;
  pageNumber: number;
  tags: AiCountTag[];
  data?: AiCountPage;
  showMarks: boolean;
  interactiveMarks?: boolean;
  onPlacementChange: (active: boolean) => void;
  focusRequest?: { id: string; sequence: number };
  onFocusMeasurement?: (id: Id<"takeoffPilotMeasurements">) => void;
  onTraceMeasurement?: (mark: AiCountPage["proposals"][number]) => void;
  onAddMeasurement?: (runId: Id<"takeoffPilotAiRuns">, tagId: Id<"takeoffPilotExtractedTags">, basis: "LF" | "SQFT") => void;
  onDrawCutout?: (mark: AiCountPage["proposals"][number]) => void;
  onRemoveCutout?: (mark: AiCountPage["proposals"][number], index: number) => void;
}) {
  const viewport = useDocumentViewport();
  const debug = useAiDebugMode().enabled;
  const start = useAction(api.takeoffPilot.tagPreview.startAiCount);
  const confirmScale = useMutation(
    api.takeoffPilot.aiPageScale.confirmAiPageScale,
  );
  const lengthRequests = useRef<Record<string, string>>({});
  const materialize = useMutation(
    api.takeoffPilot.aiMeasurements.materializeRun,
  );
  const confirm = useMutation(api.takeoffPilot.jobs.confirmAiCountScope);
  const add = useMutation(api.takeoffPilot.jobs.addAiCountMark);
  const archive = useMutation(api.takeoffPilot.jobs.archiveAiCountRun);
  const resume = useMutation(api.takeoffPilot.jobs.resumeAiCountRun);
  const reconfirm = useMutation(api.takeoffPilot.jobs.reconfirmAiCountSource);
  const [selectedMarkId, setSelectedMarkId] = useState<string | null>(null);
  const [reviewTagId, setReviewTagId] = useState<string>(preferredReviewTagId ?? data?.runs.find(run => !run.archivedAt)?.tagIds[0] ?? tags[0]?.tagId ?? "");
  const chooseReviewTag = useCallback((tagId: Id<"takeoffPilotExtractedTags">) => { setReviewTagId(tagId); setSelectedMarkId(null); }, []);
  useEffect(() => { if (preferredReviewTagId) setReviewTagId(preferredReviewTagId); }, [preferredReviewTagId]);
  useEffect(() => {
    const runs = data?.runs.filter(run => !run.archivedAt) ?? [];
    if (!preferredReviewTagId && runs.length && !runs.some(run => run.tagIds.includes(reviewTagId as Id<"takeoffPilotExtractedTags">))) setReviewTagId(runs[0].tagIds[0]);
  }, [data?.runs, preferredReviewTagId, reviewTagId]);
  const [shownIssueId, setShownIssueId] = useState<string | null>(null);
  const [newTag, setNewTag] = useState<string>("");
  const [placingRun, setPlacingRun] = useState<Id<"takeoffPilotAiRuns"> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentMark = data?.proposals.find(
    (mark) => mark._id === selectedMarkId && tags.some(tag => tag.tagId === mark.tagId),
  );
  const currentMarkLocked = isRunLocked(data, currentMark?.runId);
  useFollowNativeSelection(data, tags, nativeSelection, setSelectedMarkId, setReviewTagId);
  const reviewEditor = useRef<HTMLDivElement>(null);
  const currentMarkId = currentMark?._id;
  useEffect(() => {
    if (currentMarkId) reviewEditor.current?.scrollIntoView?.({ block: "nearest" });
  }, [currentMarkId]);
  const tagName = (id: string) =>
    tags.find((t) => t.tagId === id)?.rawValue ?? "Tag";
  const runAction = async (work: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(getUserFacingErrorMessage(e, "Unable to update AI takeoff. Your existing results have been kept."));
    } finally {
      setBusy(false);
    }
  };
  const stopPlacement = () => {
    setPlacingRun(null);
    onPlacementChange(false);
  };
  const selectMark = (id: string) => {
    setSelectedMarkId(id);
    const mark = data?.proposals.find((p) => p._id === id);
    if (mark) setReviewTagId(mark.tagId);
    if (mark?.measurementId) onFocusMeasurement?.(mark.measurementId);
    else if (mark) onSelectNative?.([]);
    stopPlacement();
  };
  const reviewMark = (values: {
    decision: "accept" | "reject";
    x?: number;
    y?: number;
    tagId?: Id<"takeoffPilotExtractedTags">;
  }) => {
    if (!currentMark) return;
    void runAction(() => onReviewBatch({
      items: [{ proposalId: currentMark._id, expectedVersion: currentMark.version, ...(values.x !== undefined ? { x: values.x } : {}), ...(values.y !== undefined ? { y: values.y } : {}) }],
      decision: values.decision,
      ...(values.tagId ? { tagId: values.tagId } : {}),
    }));
  };
  useEffect(() => {
    if (focusRequest) {
      setSelectedMarkId(focusRequest.id);
      onSelectNative?.([]);
    }
  }, [focusRequest]);
  const synchronizedFocus = useRef<typeof focusRequest>(undefined);
  useEffect(() => {
    if (!focusRequest || synchronizedFocus.current === focusRequest || selectedMarkId !== focusRequest.id) return;
    const mark = data?.proposals.find((proposal) => proposal._id === focusRequest.id);
    if (mark) {
      synchronizedFocus.current = focusRequest;
      setReviewTagId(mark.tagId);
    }
  }, [data?.proposals, focusRequest, selectedMarkId]);
  /** The run's tags still in the job, and the one chosen for a missing item (first by default). */
  const tagChoice = (run: AiCountPage["runs"][number] | undefined) => {
    const includedTagIds = run?.tagIds.filter(id => tags.some(tag => tag.tagId === id)) ?? [];
    return { includedTagIds, tagId: includedTagIds.find(id => id === newTag) ?? includedTagIds[0] };
  };
  const addMark = (runId: Id<"takeoffPilotAiRuns">, x: number, y: number) =>
    runAction(async () => {
      const { tagId } = tagChoice(data?.runs.find((r) => r._id === runId));
      if (!tagId) throw new Error("Select an item tag");
      await add({ runId, tagId, requestKey: crypto.randomUUID(), x, y });
      stopPlacement();
    });
  const drawMissingMeasurement = (run: AiCountPage["runs"][number]) => {
    const { tagId } = tagChoice(run);
    if (tagId) onAddMeasurement?.(run._id, tagId, run.measurementBasis as "LF" | "SQFT");
  };
  useEffect(() => {
    if (!controlsContainer) { setPlacingRun(null); onPlacementChange(false); }
  }, [controlsContainer, onPlacementChange]);
  const activeRuns = data?.runs.filter((run) => !run.archivedAt && tags.some(tag => run.tagIds.includes(tag.tagId) && (!reviewTagId || tag.tagId === reviewTagId))) ?? [];
  const controls = (
      <section
        data-viewport-tools-ui
        aria-label="AI takeoff"
        className="pointer-events-auto min-w-0 space-y-4 bg-background p-4 text-sm"
      >
        {!data?.enabled && (
          <Alert><AlertDescription>AI takeoff is not enabled in this environment.</AlertDescription></Alert>
        )}
        {tags.some(tag => tag.tagId === reviewTagId) && <AiReviewRunStatuses data={data} tagId={reviewTagId} />}
        {error && (
          <Alert variant="destructive"><AlertDescription role="alert">{error}</AlertDescription></Alert>
        )}
        <AiPreparationRecovery jobId={jobId} busy={busy} onAction={work => { void runAction(work); }} />
        {data && <AiReviewGroups jobId={jobId} planDocumentId={planDocumentId} pageNumber={pageNumber} data={data} nativeSelection={nativeSelection} selectedMarkId={selectedMarkId} preferredTagId={reviewTagId as Id<"takeoffPilotExtractedTags">} onTagChange={chooseReviewTag} onNavigate={onNavigateReview} onFocus={selectMark} onSelectNative={onSelectNative} onAction={work => { void runAction(work); }} onReview={onReviewBatch} onUndoLatest={onUndoLatestReview} lineageSessionId={lineageSessionId} busy={busy} />}
        {currentMark && (
          <div ref={reviewEditor}>
            <AiCountReviewEditor
              key={`${currentMark._id}:${currentMark.version}`}
              mark={currentMark}
              busy={busy}
              locked={currentMarkLocked}
              debug={debug}
              tags={tags.filter((tag) =>
                data?.runs
                  .find((run) => run._id === currentMark.runId)
                  ?.tagIds.includes(tag.tagId),
              )}
              onEditOnDrawing={currentMark.measurementId ? () => onFocusMeasurement?.(currentMark.measurementId!) : undefined}
              onTraceManually={onTraceMeasurement ? () => onTraceMeasurement(currentMark) : undefined}
              onDrawCutout={onDrawCutout}
              onRemoveCutout={onRemoveCutout}
              onReview={reviewMark}
            />
          </div>
        )}
        {data && activeRuns.map((run) => {
          const { includedTagIds, tagId: missingItemTagId } = tagChoice(run);
          const working = run.status === "queued" || run.status === "processing";
          const stale = data.staleRunIds.includes(run._id);
          const basis = run.measurementBasis ?? "count";
          const pending = data.proposals.filter(
            (p) => p.runId === run._id && p.reviewStatus === "pending_review" && (!reviewTagId || p.tagId === reviewTagId),
          );
          const issues = data.issues.filter(
            (issue) => issue.runId === run._id && issue.status === "open" && (!issue.tagId || !reviewTagId || issue.tagId === reviewTagId),
          );
          const unmaterialized = data.proposals.some((p) => p.runId === run._id && !p.measurementId);
          return (
            <section key={run._id} aria-label="Page status" className="space-y-3 border-t-2 pt-4 empty:hidden">
              {run.status === "failed" && (
                <Alert variant="destructive"><AlertDescription>AI could not finish this page. Review any results it found before starting again.</AlertDescription></Alert>
              )}
              {stale && (
                <Alert variant="warning">
                  <AlertTitle>Schedule Changed</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>Check these results against the updated schedule.</p>
                    <Button size="sm" variant="outline" disabled={busy || working} onClick={() => void runAction(() => reconfirm({ runId: run._id }))}>
                      Keep marks and restart review
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              <AiPageScaleStatus
                assessmentJson={run.scaleAssessmentJson}
                debug={debug}
                disabled={busy || working || stale}
                onConfirm={() => void runAction(() => confirmScale({ runId: run._id, expectedAssessmentJson: run.scaleAssessmentJson! }))}
              />
              {!working && !stale && run.hasSourceFrame && (
                <AiMissingItem
                  tagIds={includedTagIds}
                  tagName={tagName}
                  tagId={missingItemTagId}
                  onTagChange={setNewTag}
                  basis={basis}
                  busy={busy}
                  debug={debug}
                  placing={Boolean(placingRun)}
                  onPlace={() => { setPlacingRun(run._id); onPlacementChange(true); }}
                  onCancelPlacement={stopPlacement}
                  onDraw={onAddMeasurement ? () => drawMissingMeasurement(run) : undefined}
                  onAddAt={(x, y) => addMark(run._id, x, y)}
                />
              )}
              {debug && (
                <AiDebugPanel title={`Page Checks · ${pending.length} to review · ${issues.length} ${issues.length === 1 ? "issue" : "issues"}`}>
                  {run.canResume && (
                    <Button size="sm" disabled={busy || working || stale} onClick={() => void runAction(() => resume({ runId: run._id, expectedUpdatedAt: run.updatedAt }))}>
                      {continueLabel(run.totalTasks)}
                    </Button>
                  )}
                  {unmaterialized && (
                    <Button size="sm" variant="outline" disabled={busy || working} onClick={() => void runAction(() => materialize({ runId: run._id }))}>
                      Create draft measurements
                    </Button>
                  )}
                  <AiCountIssues
                    issues={issues}
                    disabled={busy || working || stale}
                    tagName={tagName}
                    onShowIssue={(issueId) => {
                      const bbox = data.issues.find((issue) => issue._id === issueId)?.bbox;
                      if (bbox) focusViewportOnPageBounds(viewport, { minX: bbox.x, minY: bbox.y, maxX: bbox.x + bbox.width, maxY: bbox.y + bbox.height });
                      setShownIssueId(issueId);
                    }}
                    onAction={(work) => { void runAction(work); }}
                  />
                  <AiCountScopes
                    scopes={data.scopes.filter((scope) => scope.runId === run._id && (!reviewTagId || scope.tagId === reviewTagId))}
                    disabled={busy || working || stale || !run.hasSourceFrame}
                    tagName={tagName}
                    canConfirm={(tagId) =>
                      !pending.some((mark) => mark.tagId === tagId) &&
                      !issues.some((issue) => !issue.tagId || issue.tagId === tagId)
                    }
                    onConfirm={(tagId) => void runAction(() => confirm({ runId: run._id, tagId }))}
                  />
                  {!working && canRetireUnreviewedRun(data, run._id) && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => void runAction(() => archive({ runId: run._id }))}>
                      {run.supersedesRunId ? "Discard new results and restore the original" : "Discard these results"}
                    </Button>
                  )}
                  {basis === "count" && data.proposals.some((p) => p.runId === run._id) && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || working || stale}
                      onClick={() =>
                        void runAction(async () => {
                          lengthRequests.current[run._id] ??= crypto.randomUUID();
                          await start({
                            jobId,
                            planDocumentId,
                            pageNumber,
                            selectedTagIds: run.tagIds,
                            includeExisting: run.includeExisting,
                            measurementBasis: "LF",
                            supersedesRunId: run._id,
                            requestKey: lengthRequests.current[run._id],
                          });
                          delete lengthRequests.current[run._id];
                        })
                      }
                    >
                      Measure lengths automatically
                    </Button>
                  )}
                  <AiCountAudit runId={run._id} />
                </AiDebugPanel>
              )}
            </section>
          );
        })}
        {debug && (
          <AiDebugPanel>
            <AiCountStartForm jobId={jobId} planDocumentId={planDocumentId} pageNumber={pageNumber} tags={tags} enabled={data?.enabled} busy={busy} onAction={work => { void runAction(work); }} />
          </AiDebugPanel>
        )}
      </section>
  );
  return (
    <div className="pointer-events-none absolute inset-0 z-40">
      {placingRun && (
        <div
          className="pointer-events-auto absolute inset-0 cursor-crosshair"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            const point = viewport.transforms.screenToPagePoint({
              x: event.clientX - rect.left,
              y: event.clientY - rect.top,
            });
            if (point) void addMark(placingRun, point.x, point.y);
          }}
        />
      )}
      {showMarks &&
        data?.issues
          .filter((issue) => issue._id === shownIssueId && issue.bbox)
          .map((issue) => {
            const rect = viewport.transforms.pageRectToScreenRect(issue.bbox!);
            return (
              <div
                key={issue._id}
                aria-hidden
                className="absolute border-2 border-primary bg-primary/10"
                style={{
                  left: rect.x,
                  top: rect.y,
                  width: rect.width,
                  height: rect.height,
                }}
              />
            );
          })}
      {showMarks && (
        <div inert={Boolean(placingRun)}>
          <AiCountMarks
            marks={data?.proposals.filter(mark => tags.some(tag => tag.tagId === mark.tagId)) ?? []}
            tags={tags}
            selectedMeasurementIds={nativeSelection}
            selectedMarkId={selectedMarkId}
            interactive={interactiveMarks}
            onSelect={selectMark}
          />
        </div>
      )}
      {controlsContainer && createPortal(controls, controlsContainer)}
    </div>
  );
}

/** The backend rejects review and edits while a run measures or after its source changed. */
function isRunLocked(data: AiCountPage | undefined, runId: Id<"takeoffPilotAiRuns"> | undefined) {
  const run = data?.runs.find((candidate) => candidate._id === runId);
  return Boolean(run && (run.status === "queued" || run.status === "processing" || data?.staleRunIds.includes(run._id)));
}

/** Clicking a drawn measurement selects it natively; follow it so the editor matches the item counter. */
function useFollowNativeSelection(
  data: AiCountPage | undefined,
  tags: AiCountTag[],
  nativeSelection: Id<"takeoffPilotMeasurements">[],
  setSelectedMarkId: (id: string) => void,
  setReviewTagId: (tagId: string) => void,
) {
  const mark = nativeSelection.length === 1
    ? data?.proposals.find((proposal) => proposal.measurementId === nativeSelection[0] && tags.some(tag => tag.tagId === proposal.tagId))
    : undefined;
  useEffect(() => {
    if (!mark) return;
    setSelectedMarkId(mark._id);
    setReviewTagId(mark.tagId);
    // Follow only when the selected item changes, not on every data refresh; setters are stable.
  }, [mark?._id]);
}

function AiMissingItem({ tagIds, tagName, tagId, onTagChange, basis, busy, debug, placing, onPlace, onCancelPlacement, onDraw, onAddAt }: {
  tagIds: Id<"takeoffPilotExtractedTags">[];
  tagName: (id: string) => string;
  tagId: string;
  onTagChange: (tagId: string) => void;
  basis: AiMeasurementBasis;
  busy: boolean;
  debug: boolean;
  placing: boolean;
  onPlace: () => void;
  onCancelPlacement: () => void;
  onDraw?: () => void;
  onAddAt: (x: number, y: number) => Promise<void>;
}) {
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  return (
    <div className="space-y-2">
      <p className="font-medium">Missed Something?</p>
      {tagIds.length > 1 && (
        <Select value={tagId} onValueChange={onTagChange} disabled={busy}>
          <SelectTrigger aria-label="Tag for the missing item" className="w-full"><SelectValue /></SelectTrigger>
          <SelectContent>{tagIds.map((id) => <SelectItem key={id} value={id}>{tagName(id)}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {basis !== "count" ? (
        <Button size="sm" variant="outline" disabled={busy || !onDraw} onClick={onDraw}>
          Draw missing {basis === "SQFT" ? "area" : "length"}
        </Button>
      ) : placing ? (
        <Button size="sm" variant="ghost" onClick={onCancelPlacement}>Cancel placement</Button>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={onPlace}>Place on drawing</Button>
      )}
      {debug && basis === "count" && (
        <fieldset disabled={busy} className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-2">
          <legend className="px-1 text-xs text-muted-foreground">Debug · Coordinates</legend>
          <Label className="flex items-center gap-1 text-xs">X %<Input className="w-20" type="number" min="0" max="100" step="any" value={x} onChange={(e) => setX(e.target.value)} /></Label>
          <Label className="flex items-center gap-1 text-xs">Y %<Input className="w-20" type="number" min="0" max="100" step="any" value={y} onChange={(e) => setY(e.target.value)} /></Label>
          <Button size="sm" variant="outline" disabled={!x || !y} onClick={() => void onAddAt(Number(x) / 100, Number(y) / 100).then(() => { setX(""); setY(""); })}>
            Add at coordinates
          </Button>
        </fieldset>
      )}
    </div>
  );
}

function AiPreparationRecovery({ jobId, busy, onAction }: {
  jobId: Id<"takeoffPilotJobs">;
  busy: boolean;
  onAction: (work: () => Promise<unknown>) => void;
}) {
  const workflow = useQuery(api.takeoffPilot.aiWorkflow.get, { jobId });
  const retry = useMutation(api.takeoffPilot.aiWorkflow.retryFailedPreparation);
  if (!workflow?.canRetryPreparation) return null;
  return (
    <Alert variant="warning">
      <AlertTitle>Some Pages Could Not Start</AlertTitle>
      <AlertDescription className="space-y-2">
        {workflow.preparationErrors.length > 0 && <p>{Array.from(new Set(workflow.preparationErrors)).join(" ")}</p>}
        <p>Retry to finish preparing them. Results already found are kept.</p>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction(() => retry({ jobId, version: workflow.version }))}>
          Retry failed preparation
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function AiCountScopes({
  scopes,
  disabled,
  canConfirm,
  onConfirm,
  tagName,
}: {
  scopes: AiCountPage["scopes"];
  disabled: boolean;
  canConfirm: (tagId: Id<"takeoffPilotExtractedTags">) => boolean;
  onConfirm: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  tagName: (id: string) => string;
}) {
  return scopes.map((scope) => (
    <div key={scope._id} className="flex flex-wrap items-center justify-between gap-2">
      {scope.reviewConfirmedAt ? (
        <StatusBadge status="completed" label={`${tagName(scope.tagId)} Page Reviewed`} />
      ) : (
        <Button size="sm" disabled={disabled || !canConfirm(scope.tagId)} onClick={() => onConfirm(scope.tagId)}>
          Confirm {tagName(scope.tagId)} page coverage
        </Button>
      )}
      {!scope.reviewConfirmedAt && !scope.authoritative && <span className="text-xs text-muted-foreground">Finish reviewing first</span>}
    </div>
  ));
}

function AiReviewRunStatuses({ data, tagId }: { data?: AiCountPage; tagId: string }) {
  return data?.runs.filter(run => !run.archivedAt && (!tagId || run.tagIds.includes(tagId as Id<"takeoffPilotExtractedTags">))).map(run => <AiCountRunStatus key={run._id} run={run} />);
}

const RUN_STATUS: Record<AiCountPage["runs"][number]["status"], { status: StatusKey; label: string }> = {
  queued: { status: "pending", label: "Waiting To Start" },
  processing: { status: "processing", label: "Measuring Drawing" },
  completed: { status: "review", label: "Ready For Review" },
  partial: { status: "review", label: "Partial Results" },
  failed: { status: "error", label: "Needs Attention" },
};

function AiCountRunStatus({ run }: { run: AiCountPage["runs"][number] }) {
  const { status, label } = run.status === "processing" && run.reviewStage === "reading"
    ? { status: "processing" as const, label: "Reading Drawing" }
    : RUN_STATUS[run.status];
  const percent = run.status === "processing" && run.totalTasks > 0 ? Math.round(run.completedTasks / run.totalTasks * 100) : null;
  return (
    <div role="status" className="space-y-2">
      <StatusBadge status={status} label={percent === null ? label : `${label} · ${percent}%`} showSpinner={status === "processing"} />
      {percent !== null && <Progress value={percent} aria-label="AI takeoff progress" />}
    </div>
  );
}

function canRetireUnreviewedRun(data: AiCountPage, runId: Id<"takeoffPilotAiRuns">) {
  return !data.scopes.some(scope => scope.runId === runId && scope.reviewConfirmedAt !== undefined)
    && !data.issues.some(issue => issue.runId === runId && issue.status === "resolved")
    && !data.proposals.some(mark => mark.runId === runId && (mark.editedByUser || mark.reviewStatus !== "pending_review"));
}
